import { Button, Flex } from '@wyz/ui';
import { createContext, ReactNode, useContext } from 'react';
type AcceptedValue = string | number;
type ContextValue = {
  selected: unknown;
  onSelect: (value: unknown, isSelected: boolean) => void;
};

const OptionSelectorContext = createContext<ContextValue>(null!);

type RootProps<TValue> = {
  children: ReactNode;
  selected: TValue;
  onChange?: (value: TValue | null) => void;
  clearable?: boolean;
};

function OptionSelector<TValue>({
  children,
  selected,
  onChange,
  clearable,
}: RootProps<TValue>) {
  const onSelect = (value: unknown, isSelected: boolean) => {
    const next = clearable && isSelected ? null : (value as TValue);
    onChange?.(next);
  };

  return (
    <OptionSelectorContext.Provider value={{ selected, onSelect }}>
      <Flex className='gap-2'>{children}</Flex>
    </OptionSelectorContext.Provider>
  );
}

type ItemProps<TValue extends AcceptedValue> = {
  value: TValue;
  children: ReactNode;
};

function Item<TValue extends AcceptedValue>({
  value,
  children,
}: ItemProps<TValue>) {
  const { selected, onSelect } = useContext(OptionSelectorContext);
  const isSelected = value === selected;

  return (
    <Button
      id={`select-${value}-option`}
      onClick={() => onSelect(value, isSelected)}
      variant={isSelected ? 'outline-primary' : 'neutral'}
    >
      {children}
    </Button>
  );
}

export default Object.assign(OptionSelector, { Item });
